import React, { Fragment } from 'react';
import Localize from 'react-intl-universal';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { formatPrice } from '@common/Util.jsx';

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginTop: '1rem',
  marginBottom: '1rem',
  boxShadow: 'unset',
  backgroundColor: theme.palette.booking.list,
  '&.Mui-expanded': { '&:last-of-type': { marginBottom: '1rem' } }
}));

const StyledPriceContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 8
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  fontWeight: '700',
  '& .MuiAccordionSummary-content': { display: 'block' },
  '& .MuiSvgIcon-root': { color: theme.palette.primary.light }
}));

const StyledSubheader = styled(Box)(({ theme }) => ({
  color: theme.palette.grey[600],
  fontWeight: '400',
  fontSize: '0.85rem'
}));

const StyledBooking = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.booking.info,
  padding: '0.75rem',
  paddingBottom: '0,4rem',
  marginBottom: '0.75rem',
  '& .MuiTypography-root': {
    color: theme.palette.booking.header,
    lineHeight: '0.8 ',
    fontFamily: `${theme.typography.special.fontFamily} !important`
  }
}));

const StyledBookingText = styled(Typography)(() => ({ fontWeight: '700', marginBottom: '5px' }));

const OrderSummaryWithParticipants = ({
  participants = [],
  selectedSessions,
  isEventFree,
  participantPriceAndDiscounts: {
    showDiscounts = [],
    eventName = '',
    eventPrice = 0,
    currency = ''
  }
}) => {
  const bookingPerson = participants.find((participant) => participant.isBookingPerson) ?? {
    generalInformation: {}
  };

  const firstName = bookingPerson?.generalInformation?.firstName;
  const lastName = bookingPerson?.generalInformation?.lastName;

  const participantWithoutBookingPerson = participants.filter(
    (participant) => !participant.isBookingPerson
  );

  return (
    <>
      {firstName && (
        <StyledBooking>
          <StyledBookingText>{`${firstName} ${lastName}`}</StyledBookingText>
          <StyledSubheader> {Localize.get('Booking.bookingPerson')}</StyledSubheader>
        </StyledBooking>
      )}

      {participantWithoutBookingPerson.map((participant, participantIndex) => (
        <Box key={`${Localize.get('Booking.participant')}-${participantIndex}}`}>
          <StyledAccordion square={true} defaultExpanded>
            <StyledAccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={participantIndex}
              id={participantIndex}
            >
              <Box>
                {participant?.generalInformation?.firstName}{' '}
                {participant?.generalInformation?.lastName}
              </Box>
              <StyledSubheader> {Localize.get('Booking.participant')}</StyledSubheader>
            </StyledAccordionSummary>

            <AccordionDetails>
              <Grid key={participantIndex} container>
                <Grid container item>
                  <Grid item xs={1}>
                    <Typography display='inline'>{'1x '}</Typography>
                  </Grid>
                  <Grid item xs={7} sx={{ overflowWrap: 'anywhere' }}>
                    <Typography display='inline'>{eventName}</Typography>
                  </Grid>
                  <Grid item xs={4} sx={{ flexShrink: 0, textAlign: 'end', alignContent: 'end' }}>
                    {isEventFree ? (
                      Localize.get('CustomerLoyaltyEvents.freeOfCharge')
                    ) : (
                      <StyledPriceContainer>
                        <Typography>{formatPrice(eventPrice)}</Typography>
                        <Typography>{currency}</Typography>
                      </StyledPriceContainer>
                    )}
                  </Grid>
                </Grid>

                {showDiscounts[participantIndex]?.mandatorySessions && (
                  <Grid
                    container
                    sx={{
                      fontSize: '0.8rem',
                      opacity: 0.7,
                      marginBottom: '0.7rem',
                      paddingLeft: '1rem'
                    }}
                  >
                    <Grid item xs={1}>
                      <Typography display='inline'>{'1x '}</Typography>
                    </Grid>
                    <Grid item xs={7} sx={{ overflowWrap: 'anywhere' }}>
                      <Typography display='inline'>
                        {showDiscounts[participantIndex]?.mandatorySessions?.discountName}
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ flexShrink: 0, textAlign: 'end', alignContent: 'end' }}>
                      {isEventFree ? (
                        Localize.get('CustomerLoyaltyEvents.freeOfCharge')
                      ) : (
                        <StyledPriceContainer>
                          <Typography>{`-${formatPrice(showDiscounts[participantIndex]?.mandatorySessions?.discountValue || 0)}`}</Typography>
                          <Typography>{currency}</Typography>
                        </StyledPriceContainer>
                      )}
                    </Grid>
                  </Grid>
                )}

                {selectedSessions
                  .filter((session) => !session?.optionalSession)
                  .map((selectedSession, sessionIndex) => (
                    <Fragment key={sessionIndex}>
                      <Grid item xs={1} />
                      <Grid item xs={1}>
                        <Typography sx={{ fontSize: '0.9rem', display: 'inline' }}>
                          {'1x '}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography sx={{ fontSize: '0.9rem', display: 'inline' }}>
                          {`${selectedSession?.name} (${Localize.get('Booking.mandatory')})`}
                        </Typography>
                      </Grid>
                      <Grid item xs={1} />
                    </Fragment>
                  ))}

                {selectedSessions
                  .filter((session) => session?.optionalSession)
                  .map((selectedSession, sessionIndex) => (
                    <Fragment key={sessionIndex}>
                      <Grid item xs={1}>
                        <Typography display='inline'>{'1x '}</Typography>
                      </Grid>
                      <Grid item xs={7} sx={{ overflowWrap: 'anywhere' }}>
                        <Typography display='inline'>{`${selectedSession?.name} (${Localize.get('Booking.optional')})`}</Typography>
                      </Grid>
                      <Grid
                        item
                        xs={4}
                        sx={{ flexShrink: 0, textAlign: 'end', alignContent: 'end' }}
                      >
                        {isEventFree ? (
                          Localize.get('CustomerLoyaltyEvents.freeOfCharge')
                        ) : (
                          <StyledPriceContainer>
                            <Typography>{formatPrice(selectedSession?.price)}</Typography>
                            <Typography>{currency}</Typography>
                          </StyledPriceContainer>
                        )}
                      </Grid>
                      {showDiscounts[participantIndex]?.optionalSessions?.length > 0 &&
                        showDiscounts[participantIndex]?.optionalSessions?.find(
                          (item) => item.id === selectedSession.id
                        ) && (
                          <Grid
                            container
                            sx={{
                              fontSize: '0.8rem',
                              opacity: 0.7,
                              marginBottom: '0.7rem',
                              paddingLeft: '1rem'
                            }}
                          >
                            <Grid item xs={1}>
                              <Typography display='inline'>{'1x '}</Typography>
                            </Grid>
                            <Grid item xs={7} sx={{ overflowWrap: 'anywhere' }}>
                              <Typography display='inline'>
                                {
                                  showDiscounts[participantIndex]?.optionalSessions?.find(
                                    (item) => item.id === selectedSession.id
                                  )?.discountName
                                }
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              xs={4}
                              sx={{ flexShrink: 0, textAlign: 'end', alignContent: 'end' }}
                            >
                              {isEventFree ? (
                                Localize.get('CustomerLoyaltyEvents.freeOfCharge')
                              ) : (
                                <StyledPriceContainer>
                                  <Typography>{`-${formatPrice(
                                    showDiscounts[participantIndex]?.optionalSessions?.find(
                                      (item) => item.id === selectedSession.id
                                    )?.discountValue
                                  )}`}</Typography>
                                  <Typography>{currency}</Typography>
                                </StyledPriceContainer>
                              )}
                            </Grid>
                          </Grid>
                        )}
                    </Fragment>
                  ))}
              </Grid>
            </AccordionDetails>
          </StyledAccordion>
        </Box>
      ))}
    </>
  );
};
export default OrderSummaryWithParticipants;
